<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import type { Note } from '../../api/collab/types'
import type { ChatMessage, LearnItem, SystemEvent } from '../../api/types'
import { notesInThread } from '../../lib/collab'
import { chatLink, copyText } from '../../lib/chatLink'
import { dayKey, dayLabel } from '../../lib/inbox'
import { QUICK_REPLY_LABEL, quickReply, quickReplyTitle, waitedText } from '../../lib/style'
import { useClock } from '../../stores/clock'
import { sendStatusOf } from '../../lib/send'
import { useAiMode } from '../../stores/aimode'
import { useCollab } from '../../stores/collab'
import { useInbox, type UiItem } from '../../stores/inbox'
import AiBusyRow from '../aimode/AiBusyRow.vue'
import AiModeBar from '../aimode/AiModeBar.vue'
import AiModeButton from '../aimode/AiModeButton.vue'
import CollabBar from '../collab/CollabBar.vue'
import NoteComposer from '../collab/NoteComposer.vue'
import NoteLine from '../collab/NoteLine.vue'
import Avatar from '../ui/Avatar.vue'
import Icon from '../ui/Icon.vue'
import ChatMoreMenu from './ChatMoreMenu.vue'
import ChatTagButton from './ChatTagButton.vue'
import CommentChatBar from './CommentChatBar.vue'
import FbHistoryBar from './FbHistoryBar.vue'
import LineHistoryBar from './LineHistoryBar.vue'
import Composer from './Composer.vue'
import LearnCard from './LearnCard.vue'
import MessageBubble from './MessageBubble.vue'
import PlatformLink from './PlatformLink.vue'
import SystemLine from './SystemLine.vue'

const store = useInbox()
const clock = useClock()
const collab = useCollab()

const conv = computed(() => store.selected)
const detail = computed(() => store.selectedDetail)
const id = computed(() => store.selectedId)

const source = computed(() => detail.value?.ad_source?.label ?? detail.value?.account.name ?? null)
const closed = computed(() => conv.value?.status === 'closed')
/** แนวลูกค้า "ห้วน" waiting for our reply (lib/style.ts): "ตอบภายใน 5 นาที · ลูกค้ารอ 3 นาที", warning after 5 minutes. */
const quick = computed(() => quickReply(conv.value, clock.now))

// ---- thread rows: day separators + grouping (same author within 2 min → one avatar, one time)
type Row =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'sys'; key: string; item: SystemEvent }
  | { kind: 'learn'; key: string; item: LearnItem }
  | { kind: 'note'; key: string; item: Note }
  | { kind: 'msg'; key: string; item: ChatMessage & UiItem; showAvatar: boolean; showMeta: boolean; tight: boolean }

const who = (m: ChatMessage): string =>
  m.direction === 'in'
    ? `c:${m.author.kind === 'contact' ? (m.author.contact?.id ?? '') : ''}` // LINE group: one group per member
    : m.author.kind === 'staff'
      ? `s:${m.author.staff.id}`
      : m.author.kind

function sameGroup(a: UiItem | undefined, b: UiItem | undefined): boolean {
  if (!a || !b || a.kind !== 'message' || b.kind !== 'message') return false
  // AI bubbles group like a person's (one "AI" label per burst · docs/ai-mode.md); who() tells AI from staff
  if (sendStatusOf(a) === 'failed' || sendStatusOf(b) === 'failed') return false
  if (who(a) !== who(b) || dayKey(a.at) !== dayKey(b.at)) return false
  return Math.abs(Date.parse(b.at) - Date.parse(a.at)) <= 120000
}

const rows = computed<Row[]>(() => {
  const items = store.selectedThread ?? []
  const out: Row[] = []
  let lastDay = ''
  // internal notes written in this chat sit in the timeline at their time (staff only, stores/collab.ts)
  const notes = id.value ? notesInThread(collab.notesFor(id.value), id.value, items, !!store.olderCursor[id.value]) : []
  let ni = 0
  const flushNotes = (upTo: string | null) => {
    while (ni < notes.length && (upTo === null || notes[ni]!.at <= upTo)) {
      const n = notes[ni++]!
      const d = dayKey(n.at)
      if (d !== lastDay) {
        out.push({ kind: 'day', key: `d${d}`, label: dayLabel(n.at, clock.now) })
        lastDay = d
      }
      out.push({ kind: 'note', key: `n${n.id}`, item: n })
    }
  }
  items.forEach((it, i) => {
    flushNotes(it.at)
    const d = dayKey(it.at)
    if (d !== lastDay) {
      out.push({ kind: 'day', key: `d${d}`, label: dayLabel(it.at, clock.now) })
      lastDay = d
    }
    if (it.kind === 'system') {
      out.push({ kind: 'sys', key: it.id, item: it })
      return
    }
    if (it.kind === 'learn') {
      // "/ln" from a test account: a staff-only card, never a customer bubble (api/app/ai/learn.py)
      out.push({ kind: 'learn', key: `l${it.id}`, item: it })
      return
    }
    const prev = items[i - 1]
    const next = items[i + 1]
    const grouped = sameGroup(it, next)
    out.push({ kind: 'msg', key: it.id, item: it, showAvatar: !grouped, showMeta: !grouped, tight: sameGroup(prev, it) })
  })
  flushNotes(null)
  return out
})

// ---- scroll: stick to the bottom on open and when new messages arrive (if already near it)
const scroller = ref<HTMLElement | null>(null)
const inner = ref<HTMLElement | null>(null)
let nearBottom = true
function onScroll(): void {
  const el = scroller.value
  if (el) nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80
}
function toBottom(): void {
  const el = scroller.value
  if (el) el.scrollTop = el.scrollHeight
}
// a click / key in the thread may open something under it (a card opening): read it from where it opens
let touched = 0
function onTouch(): void {
  touched = Date.now()
}
watch(id, () => {
  nearBottom = true
  void nextTick(toBottom)
})
// AI mode (docs/ai-mode.md): may the AI take this chat / what it understood — again when its state changes
const aimode = useAiMode()
// the "AI กำลังคิด" row comes and goes at the bottom: stay pinned to the end like for a new bubble
watch(
  () => (id.value ? aimode.busyOf(id.value) : null),
  () => {
    if (nearBottom) void nextTick(toBottom)
  },
)
watch(
  () => [id.value, conv.value?.ai_mode?.session_id ?? null, conv.value?.ai_handoff?.at ?? null, conv.value?.status] as const,
  ([cid, sid], old) => {
    if (!cid) return
    void aimode.load(cid)
    // the AI just let go of the chat I am looking at: the staff answers now, bring the usual draft back
    if (old && old[0] === cid && old[1] && !sid) void store.loadDraft(cid)
  },
  { immediate: true },
)
// the area under the thread changes height (follow-up card, note mode, a long draft), or the thread itself grows after
// it was drawn (a picture loads, the slip check / phone / menu rows under a bubble arrive): stay pinned to the end
let resize: ResizeObserver | null = null
onMounted(() => {
  if (typeof ResizeObserver === 'undefined') return
  resize = new ResizeObserver((entries) => {
    if (!nearBottom) return
    // grew right after a click in it: stay, and no longer count as "at the end" until scrolled back there
    if (entries.every((e) => e.target === inner.value) && Date.now() - touched < 1000) return onScroll()
    toBottom()
  })
  for (const box of [scroller, inner]) {
    watch(
      box,
      (el, old) => {
        if (old) resize?.unobserve(old)
        if (el) resize?.observe(el)
      },
      { immediate: true },
    )
  }
})
onBeforeUnmount(() => resize?.disconnect())
watch(
  () => (store.selectedThread?.length ?? 0) + (id.value ? collab.notesFor(id.value).length * 1000 : 0),
  () => {
    if (nearBottom) void nextTick(toBottom)
  },
)

// ---- header actions
async function copyLink(): Promise<void> {
  if (!id.value) return
  const ok = await copyText(chatLink(id.value))
  store.flash(ok ? 'คัดลอกลิงก์แชทแล้ว · ส่งให้คนในทีมที่เข้าระบบแชทได้' : 'คัดลอกไม่สำเร็จ ลองอีกครั้ง')
}
const moveOpen = ref(false)
function moveTo(staffId: string): void {
  moveOpen.value = false
  if (id.value) void store.reassign(id.value, staffId)
}
const olderAvailable = computed(() => (id.value ? !!store.olderCursor[id.value] : false))
</script>

<template>
  <main class="chat" :aria-label="conv ? `แชทกับ${conv.contact.name}` : 'แชท'">
    <div v-if="!id" class="blank">
      <Icon name="chat" />
      <p>เลือกแชททางซ้ายเพื่อเริ่มตอบ</p>
    </div>

    <div v-else-if="store.openError && !conv" class="blank">
      <Icon name="alertCircle" />
      <p>เปิดแชทไม่สำเร็จ: {{ store.openError }}</p>
      <button class="btn" @click="store.select(id)">ลองใหม่</button>
    </div>

    <template v-else-if="conv">
      <header class="ch-head">
        <Avatar :initial="conv.contact.initial" :url="conv.contact.avatar_url" :contact-id="conv.contact.id" :size="40" />
        <div class="chh-id">
          <div class="chh-name">
            <h2>{{ conv.contact.name }}</h2>
            <!-- the logo opens this chat on LINE OA / Meta Business Suite in a new tab (docs/api-contract.md) -->
            <PlatformLink :conv-id="conv.id" :channel="conv.channel" :kind="conv.kind" :history="!!detail?.is_history" :name="conv.contact.name" />
          </div>
          <div class="chh-sub">
            <!-- two items of the small line: on a narrow chat column (1280 px) the wait shares the next line with
                 "ดูแลโดย …" instead of going under the buttons -->
            <template v-if="quick">
              <span class="cq-b" :class="{ late: quick.late }" :title="quickReplyTitle(quick)"
                ><Icon name="zap" />{{ QUICK_REPLY_LABEL }}</span
              >
              <span class="cq-w" :class="{ late: quick.late }">{{ waitedText(quick) }}</span>
            </template>
            <span v-if="conv.assigned_staff">ดูแลโดย <b>{{ conv.assigned_staff.name }}</b></span>
            <span v-else>ยังไม่มีคนดูแล</span>
            <!-- on the small line, not among the big buttons: the customer's name must never be cut (1280 px) -->
            <button
              class="chh-link"
              aria-label="คัดลอกลิงก์แชท"
              title="คัดลอกลิงก์แชทนี้ ส่งให้คนในทีม (เปิดได้เฉพาะคนที่เข้าระบบแชท)"
              @click="copyLink"
            >
              <Icon name="link" /><span>ลิงก์แชท</span>
            </button>
            <span v-if="source" class="chh-src">มาจาก {{ source }}</span>
          </div>
        </div>
        <!-- a history chat (LINE export / Facebook history) is read-only: no assign / close / AI actions -->
        <div v-if="!detail?.is_history" class="chh-act">
          <!-- "ติดแท็ก" — used often (owner 18 ต.ค. 69), out of the "…" menu -->
          <ChatTagButton v-if="detail" :detail="detail" />
          <AiModeButton :conv-id="conv.id" />
          <div class="move">
            <button class="btn move-b" aria-haspopup="menu" aria-label="ย้ายแชท" title="ย้ายแชทให้คนอื่น" :aria-expanded="moveOpen" @click="moveOpen = !moveOpen">
              <Icon name="swap" /><span class="move-l">ย้ายแชท</span>
            </button>
            <div v-if="moveOpen" class="pop-back" @click="moveOpen = false"></div>
            <ul v-if="moveOpen" class="pop" role="menu" aria-label="ย้ายแชทให้" @keydown.esc="moveOpen = false">
              <li v-for="m in store.team" :key="m.id">
                <button
                  role="menuitem"
                  class="pop-i"
                  :disabled="!m.available || m.id === conv.assigned_staff?.id"
                  @click="moveTo(m.id)"
                >
                  <span class="av pop-av" :class="store.staffInfo(m.id).tone">{{ m.initial }}</span>
                  <span class="grow">{{ m.name }}</span>
                  <span class="muted">{{ m.available ? `${m.open_count}/${m.cap}` : 'พัก' }}</span>
                </button>
              </li>
            </ul>
          </div>
          <button
            class="btn ico"
            :class="{ pressed: detail?.auto_close_off }"
            :aria-pressed="!!detail?.auto_close_off"
            aria-label="ห้ามปิดอัตโนมัติ"
            :title="detail?.auto_close_off ? 'ห้ามปิดอัตโนมัติ (เปิดอยู่)' : 'ห้ามปิดอัตโนมัติ'"
            @click="store.toggleAutoCloseOff(conv.id)"
          >
            <Icon name="pin" />
          </button>
          <button v-if="!closed" class="btn btn-close" @click="store.setStatus(conv.id, 'closed')">
            <Icon name="checkCircle" />ปิดแชท
          </button>
          <button v-else class="btn" @click="store.setStatus(conv.id, 'open')"><Icon name="repeat" />เปิดแชทอีกครั้ง</button>
          <!-- "…": internal note, block (web), ล้างแชท (test accounts) — moved out of the side panel 17 ต.ค. 69 -->
          <ChatMoreMenu v-if="detail" :detail="detail" />
        </div>
      </header>
      <AiModeBar :conv-id="conv.id" />
      <!-- a read-only Facebook / Instagram history chat (api/app/fb_history.py): what it is + back to the live chat -->
      <FbHistoryBar v-if="detail?.is_history && (conv.channel === 'fb' || conv.channel === 'ig')" :detail="detail" />
      <!-- an imported LINE history room (api/app/line_link.py): what it is, whose it is + back to the live chat -->
      <LineHistoryBar v-if="detail?.is_history && conv.channel === 'line' && conv.kind === 'dm'" :detail="detail" />

      <div ref="scroller" class="thread scroll" @scroll.passive="onScroll" @pointerdown.passive="onTouch" @keydown="onTouch">
        <div ref="inner" class="thread-in">
          <button v-if="olderAvailable" class="older" @click="store.loadOlder(conv.id)">ดูข้อความก่อนหน้า</button>
          <p v-if="!store.selectedThread" class="loading">กำลังโหลดข้อความ…</p>
          <template v-for="r in rows" :key="r.key">
            <div v-if="r.kind === 'day'" class="day"><span>{{ r.label }}</span></div>
            <SystemLine v-else-if="r.kind === 'sys'" :item="r.item" />
            <NoteLine v-else-if="r.kind === 'note'" :note="r.item" :conv-id="conv.id" />
            <LearnCard v-else-if="r.kind === 'learn'" :item="r.item" />
            <MessageBubble
              v-else
              :item="r.item"
              :contact-initial="conv.contact.initial"
              :contact="conv.kind === 'group' ? null : conv.contact"
              :staff="r.item.author.kind === 'staff' ? store.staffInfo(r.item.author.staff.id) : null"
              :show-avatar="r.showAvatar"
              :show-meta="r.showMeta"
              :tight="r.tight"
              @dismiss="(mid) => store.dismissFailed(conv!.id, mid)"
              @retry="(mid) => store.retrySend(conv!.id, mid)"
            />
          </template>
          <!-- "AI กำลังคิดคำตอบ / กำลังพิมพ์" where the AI's next bubble will land (docs/ai-mode.md) -->
          <AiBusyRow :conv-id="conv.id" />
        </div>
      </div>

      <CommentChatBar v-if="conv.kind === 'comment'" :conv-id="conv.id" />
      <template v-else-if="!detail?.is_history">
        <CollabBar :conv-id="conv.id" />
        <NoteComposer v-if="collab.noteMode[conv.id]" :conv-id="conv.id" />
        <Composer v-else :conv-id="conv.id" />
      </template>
    </template>
  </main>
</template>

<style scoped>
.chat {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background: var(--bg);
  container-type: inline-size;
}
/* narrow chat column (1280/1440 wide screens, with the "…" menu since 17 ต.ค. 69): icon-only move button (its
   aria-label / title say "ย้ายแชท"), sub line may wrap */
@container (max-width: 600px) {
  .move-l {
    display: none;
  }
  .move-b {
    width: 32px;
    padding: 0;
    justify-content: center;
  }
}
.blank {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: var(--mut);
  font-size: 14px;
  line-height: 22px;
}
.blank :deep(.ic) {
  width: 32px;
  height: 32px;
  stroke-width: 1.4;
  color: #9fb3a9;
}
.ch-head {
  min-height: 68px;
  flex: none;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 2px 16px 2px 24px;
  background: #fff;
  border-bottom: 1px solid var(--line);
}
.chh-id {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.chh-name {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.chh-name h2 {
  min-width: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* "ดูแลโดย …" and "มาจาก …" each start with their own word, so no dot between them: when a long page/ad name
   wraps to a second line nothing dangles at the end of the first (3 lines still fit the 68 px header) */
.chh-sub {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 12px;
  min-width: 0;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
  white-space: nowrap;
}
.chh-sub b {
  color: var(--ink);
  font-weight: 600;
}
.chh-src {
  min-width: 0;
  white-space: normal;
}
/* แนวลูกค้า "ห้วน" waiting for us (lib/style.ts): calm until 5 minutes, then the warning colour */
.cq-b {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 22px;
  padding: 0 8px 0 6px;
  border: 1px solid #b9d7ca;
  border-radius: 6px;
  background: #f1f8f4;
  color: #0a5e47;
  font-size: 12px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
}
.cq-b.late {
  border-color: transparent;
  background: var(--warns);
  color: #7a4700;
}
.cq-w.late {
  color: var(--warn);
  font-weight: 600;
}
.cq-b :deep(.ic) {
  width: 13px;
  height: 13px;
  stroke-width: 2.2;
}
.chh-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
  margin: 0 -4px;
  border-radius: 6px;
  font-size: 12.5px;
  line-height: 19px;
  color: var(--mut);
}
.chh-link:hover {
  color: var(--ink);
  background: var(--soft, rgba(0, 0, 0, 0.05));
}
.chh-link :deep(.ic) {
  width: 13px;
  height: 13px;
}
.chh-act {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: none;
}
.btn-close {
  border-color: var(--ac);
  color: var(--ac);
  font-weight: 600;
}
.btn-close :deep(.ic) {
  color: var(--ac);
}
.btn.pressed {
  background: var(--acs);
  border-color: #9fcdb8;
}
.btn.pressed :deep(.ic) {
  color: var(--ac2);
}
.move {
  position: relative;
}
.pop-back {
  position: fixed;
  inset: 0;
  z-index: 20;
}
.pop {
  position: absolute;
  right: 0;
  top: 38px;
  z-index: 21;
  min-width: 200px;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(23, 33, 28, 0.12);
}
.pop-i {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  height: 36px;
  padding: 0 8px;
  border-radius: 7px;
  font-size: 13px;
  line-height: 20px;
  white-space: nowrap;
}
.pop-i:hover:not(:disabled) {
  background: #f4f6f5;
}
.pop-i:disabled {
  opacity: 0.55;
}
.pop-av {
  width: 22px;
  height: 22px;
  font-size: 12px;
}
.pop-i .muted {
  font-size: 12px;
}
.thread {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 16px 24px;
}
.thread-in {
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.day {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  line-height: 18px;
  color: var(--mut);
}
.day::before,
.day::after {
  content: '';
  flex: 1;
  height: 1px;
  background: var(--line);
}
.older {
  align-self: center;
  height: 28px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid var(--line);
  background: #fff;
  font-size: 12px;
  color: var(--mut);
}
.loading {
  align-self: center;
  font-size: 13px;
  color: var(--mut);
}
</style>
